import React from "react";
import { Youtube, Instagram, Twitter, Music2, Mail } from "lucide-react";
import { useTeamFilter } from "@/context/TeamFilterContext";

const platforms = [
  { name: "YouTube", icon: Youtube, key: "yt", url: "https://www.youtube.com/@halosintheinfield" },
  { name: "Substack", icon: Mail, key: "ss", url: "https://substack.com/@angelsbaseballhiti" },
  { name: "Instagram", icon: Instagram, key: "ig", url: "https://www.instagram.com/halos_in_the_infield/" },
  { name: "Twitter / X", icon: Twitter, key: "tw", url: "https://x.com/HalosInfield" },
  { name: "TikTok", icon: Music2, key: "tt", url: "https://www.tiktok.com/@halos_in_the_infield" },
];

export default function AboutBanner() {
  const { team, toggle } = useTeamFilter();
  const isAngels = team === "angels";
  const isDucks = team === "ducks";
  const isNfl = team === "nfl";
  return (
    <section
      className="relative overflow-hidden bg-halo-navy text-halo-cream border-b-4 border-halo-red"
      data-testid="about-banner"
    >
      {/* Subtle grain overlay */}
      <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay pointer-events-none"
           style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fff 1px, transparent 1px)", backgroundSize: "3px 3px" }} />

      <div className="relative px-6 md:px-12 lg:px-16 py-12 md:py-14">
        {/* Collaboration logos */}
        <div className="flex items-center justify-center gap-4 sm:gap-8 mb-10 pb-8 border-b border-halo-navyLight flex-wrap" data-testid="collab-logos">
          <button
            type="button"
            onClick={() => toggle("angels")}
            data-testid="filter-angels-btn"
            aria-pressed={isAngels}
            className={`group flex flex-col items-center gap-2 p-3 border-2 transition-all ${
              isAngels
                ? "border-halo-red bg-halo-red/10 scale-105"
                : team !== "all" && !isAngels
                  ? "border-transparent opacity-40 hover:opacity-70"
                  : "border-transparent hover:border-halo-red/40"
            }`}
          >
            <img
              src="https://a.espncdn.com/i/teamlogos/mlb/500/laa.png"
              alt="Los Angeles Angels"
              className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 object-contain drop-shadow-[0_4px_12px_rgba(186,0,33,0.4)] transition-transform group-hover:scale-105"
              data-testid="logo-angels"
            />
            <span className={`font-sports uppercase tracking-[0.25em] text-[10px] sm:text-xs ${isAngels ? "text-halo-red" : "text-halo-red/80"}`}>
              {isAngels ? "Showing Angels" : "Filter Angels"}
            </span>
          </button>

          <div className="flex flex-col items-center gap-1">
            <span className="font-sports uppercase tracking-[0.4em] text-2xl sm:text-3xl text-halo-cream/40">×</span>
          </div>

          <button
            type="button"
            onClick={() => toggle("ducks")}
            data-testid="filter-ducks-btn"
            aria-pressed={isDucks}
            className={`group flex flex-col items-center gap-2 p-3 border-2 transition-all ${
              isDucks
                ? "border-[#F47A38] bg-[#F47A38]/10 scale-105"
                : team !== "all" && !isDucks
                  ? "border-transparent opacity-40 hover:opacity-70"
                  : "border-transparent hover:border-[#F47A38]/40"
            }`}
          >
            <img
              src="https://a.espncdn.com/i/teamlogos/nhl/500/ana.png"
              alt="Anaheim Ducks"
              className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 object-contain drop-shadow-[0_4px_12px_rgba(244,122,56,0.4)] transition-transform group-hover:scale-105"
              data-testid="logo-ducks"
            />
            <span className={`font-sports uppercase tracking-[0.25em] text-[10px] sm:text-xs ${isDucks ? "text-[#F47A38]" : "text-[#F47A38]/80"}`}>
              {isDucks ? "Showing Ducks" : "Filter Ducks"}
            </span>
          </button>

          <div className="flex flex-col items-center gap-1">
            <span className="font-sports uppercase tracking-[0.4em] text-2xl sm:text-3xl text-halo-cream/40">×</span>
          </div>

          <button
            type="button"
            onClick={() => toggle("nfl")}
            data-testid="filter-nfl-btn"
            aria-pressed={isNfl}
            className={`group flex flex-col items-center gap-2 p-3 border-2 transition-all ${
              isNfl
                ? "border-halo-cream bg-halo-cream/10 scale-105"
                : team !== "all" && !isNfl
                  ? "border-transparent opacity-40 hover:opacity-70"
                  : "border-transparent hover:border-halo-cream/40"
            }`}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_halo-infield/artifacts/i3pvsij4_ChatGPT%20Image%20Jul%206%2C%202026%2C%2009_13_07%20PM.png"
              alt="NFL H.I.T.I. Unfiltered"
              className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 object-contain drop-shadow-[0_4px_12px_rgba(248,248,245,0.35)] transition-transform group-hover:scale-105"
              data-testid="logo-nfl"
            />
            <span className={`font-sports uppercase tracking-[0.25em] text-[10px] sm:text-xs ${isNfl ? "text-halo-cream" : "text-halo-cream/80"}`}>
              {isNfl ? "Showing NFL" : "Filter NFL"}
            </span>
          </button>
        </div>

        {team !== "all" && (
          <div className="mb-6 flex items-center justify-center gap-3" data-testid="filter-active-indicator">
            <span className="font-sports uppercase tracking-widest text-xs text-halo-cream/60">
              Filtering content by:
            </span>
            <button
              onClick={() => toggle(team)}
              data-testid="clear-filter-btn"
              className={`font-sports uppercase tracking-wider text-xs px-3 py-1 border ${
                team === "angels"
                  ? "border-halo-red text-halo-red bg-halo-red/10"
                  : team === "ducks"
                    ? "border-[#F47A38] text-[#F47A38] bg-[#F47A38]/10"
                    : "border-halo-cream text-halo-cream bg-halo-cream/10"
              }`}
            >
              {team === "angels" ? "⚾ Angels" : team === "ducks" ? "🏒 Ducks" : "🏈 NFL"} · Clear ✕
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 text-halo-red font-sports uppercase tracking-[0.3em] text-xs mb-4">
            <span className="w-8 h-px bg-halo-red" />
            <span>The HITI Network</span>
          </div>
          <h2 className="font-sports uppercase tracking-tight leading-[0.95] text-3xl sm:text-4xl lg:text-5xl text-balance">
            Covering the <span className="text-halo-red">Angels</span>, the <span className="text-[#F47A38]">Ducks</span> &amp; <span className="text-halo-cream">the NFL</span>
            <br className="hidden sm:block" />
            on every platform.
          </h2>
          <p className="mt-5 font-body text-halo-cream/75 text-base md:text-lg max-w-2xl leading-relaxed">
            Halos in the Infield is your home for independent fan coverage of <strong className="text-halo-cream">Anaheim's
            two teams</strong>. Live postgame shows, deep-dive podcasts, breaking news takes, newsletter columns and
            short-form clips — all in one place. Baseball red. Hockey orange. One section.
          </p>
        </div>

        <div className="lg:col-span-5">
          <div className="font-sports uppercase tracking-[0.25em] text-[10px] text-halo-cream/50 mb-4">
            Find Us Across
          </div>
          <div className="grid grid-cols-5 gap-2">
            {platforms.map((p) => {
              const Icon = p.icon;
              return (
                <a
                  key={p.key}
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`about-platform-${p.key}`}
                  className="flex flex-col items-center justify-center gap-2 aspect-square border border-halo-navyLight hover:border-halo-red hover:bg-halo-navyLight transition-colors"
                  title={p.name}
                  aria-label={p.name}
                >
                  <Icon className="w-5 h-5 lg:w-6 lg:h-6" strokeWidth={2} />
                  <span className="font-sports uppercase tracking-wider text-[9px] lg:text-[10px] text-halo-cream/70 text-center leading-tight px-1">
                    {p.name}
                  </span>
                </a>
              );
            })}
          </div>
          <div className="mt-5 grid grid-cols-3 gap-2 text-xs font-sports uppercase tracking-widest">
            <div className="bg-halo-red/15 border border-halo-red/40 text-halo-red px-2 py-2 flex items-center justify-between gap-1">
              <span>⚾ Angels</span><span className="text-halo-cream/60">MLB</span>
            </div>
            <div className="bg-[#F47A38]/15 border border-[#F47A38]/40 text-[#F47A38] px-2 py-2 flex items-center justify-between gap-1">
              <span>🏒 Ducks</span><span className="text-halo-cream/60">NHL</span>
            </div>
            <div className="bg-halo-cream/10 border border-halo-cream/40 text-halo-cream px-2 py-2 flex items-center justify-between gap-1">
              <span>🏈 NFL</span><span className="text-halo-cream/60">Unfiltered</span>
            </div>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}
